/**
 * Structured Logger
 *
 * A simple structured logger for consistent logging across the platform.
 * Can be replaced with Pino or Winston in production.
 */

import { Logger, LoggerConfig, LogEntry, LogLevel } from './types';

// Declare process for browser compatibility
declare const process: { env?: Record<string, string | undefined> } | undefined;

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

/**
 * Default logger implementation
 */
class DefaultLogger implements Logger {
  private config: Required<LoggerConfig>;

  constructor(config: LoggerConfig) {
    this.config = {
      level: config.level,
      context: config.context ?? '',
      pretty:
        config.pretty ??
        (typeof process !== 'undefined' ? process.env?.['NODE_ENV'] !== 'production' : true),
    };
  }

  private shouldLog(level: LogLevel): boolean {
    return LOG_LEVELS[level] >= LOG_LEVELS[this.config.level];
  }

  private formatEntry(entry: LogEntry): string {
    if (this.config.pretty) {
      const prefix = entry.context ? `[${entry.context}]` : '';
      const level = entry.level.toUpperCase().padEnd(5);
      const dataStr = entry.data ? ` ${JSON.stringify(entry.data)}` : '';
      const errorStr = entry.error
        ? `\n  ${entry.error.name}: ${entry.error.message}${entry.error.stack ? `\n${entry.error.stack}` : ''}`
        : '';
      return `${entry.timestamp} ${level} ${prefix} ${entry.message}${dataStr}${errorStr}`;
    }
    return JSON.stringify(entry);
  }

  private log(
    level: LogLevel,
    message: string,
    data?: Record<string, unknown>,
    error?: Error
  ): void {
    if (!this.shouldLog(level)) return;

    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date().toISOString(),
      context: this.config.context || undefined,
      data,
      error: error
        ? {
            name: error.name,
            message: error.message,
            stack: error.stack,
          }
        : undefined,
    };

    const formatted = this.formatEntry(entry);

    switch (level) {
      case 'debug':
        console.debug(formatted);
        break;
      case 'info':
        console.info(formatted);
        break;
      case 'warn':
        console.warn(formatted);
        break;
      case 'error':
        console.error(formatted);
        break;
    }
  }

  debug(message: string, data?: Record<string, unknown>): void {
    this.log('debug', message, data);
  }

  info(message: string, data?: Record<string, unknown>): void {
    this.log('info', message, data);
  }

  warn(message: string, data?: Record<string, unknown>): void {
    this.log('warn', message, data);
  }

  error(message: string, error?: Error, data?: Record<string, unknown>): void {
    this.log('error', message, data, error);
  }

  child(context: string): Logger {
    const newContext = this.config.context ? `${this.config.context}:${context}` : context;
    return new DefaultLogger({ ...this.config, context: newContext });
  }
}

/**
 * Create a logger instance
 */
export function createLogger(config: Partial<LoggerConfig> = {}): Logger {
  const envLogLevel = typeof process !== 'undefined' ? process.env?.['LOG_LEVEL'] : undefined;
  return new DefaultLogger({
    level: config.level ?? (envLogLevel as LogLevel) ?? 'info',
    context: config.context,
    pretty: config.pretty,
  });
}

/**
 * Default logger instance
 */
export const logger = createLogger();
